import { CampaignType, CheckoutEligibility, RewardAmountType, RewardUnlockStrategy } from '@brandclub/common-ui';
import type {
  CatalogV2CampaignReward,
  CatalogV2Offer,
  CatalogV2Product,
  CatalogV2Variant,
  ProductRewardSearchResult,
} from '@brandclub/common-ui';

const asPrice = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const toCheckoutEligibility = (eligible: boolean | null | undefined): CheckoutEligibility =>
  eligible === true ? CheckoutEligibility.SERVER_SIDE : CheckoutEligibility.NOT_ELIGIBLE;

const toReward = (reward: CatalogV2CampaignReward) => ({
  campaignId: reward.campaignId,
  campaignStatus: reward.campaignStatus ?? 'live',
  campaignType: reward.campaignType as CampaignType,
  rewardAmount: reward.rewardAmount ?? 0,
  rewardAmountType: reward.rewardAmountType === 'percent' ? RewardAmountType.Percent : RewardAmountType.Fixed,
  rewardUnlockStrategy:
    reward.rewardUnlockStrategy === 'instant' ? RewardUnlockStrategy.Instant : RewardUnlockStrategy.BrandedPurchase,
  rewardPercent: reward.rewardPercent,
});

const offerToMatchedProduct = (offer: CatalogV2Offer, fallback: CatalogV2Variant): Record<string, unknown> => ({
  retailerId: offer.retailerId ?? 0,
  retailerSku: offer.retailerSku,
  retailerDisplayName: offer.retailerName,
  retailPrice: asPrice(offer.retailPrice),
  checkoutEligibility: CheckoutEligibility.NOT_ELIGIBLE,
  isAvailable: offer.inStock !== false,
  hasFirstPartyOffer: false,
  stacklineSku: offer.stacklineSku ?? fallback.stacklineSku,
  title: offer.title ?? fallback.title,
  imageUrls: offer.imageUrls?.length ? offer.imageUrls : fallback.imageUrls,
  retailerProductPageUrl: offer.retailerProductPageUrl,
});

/**
 * Which foreign offers to surface as "ways to buy" — the same rule the canonical Pages serve
 * path applies (`productComposer.offersToShow`), so the buy box, the "more retailers" strip and
 * the store locator can't disagree with a served page:
 *  - **first-class**: offers whose retailer is link-out (WHERE_TO_BUY) enabled for this product;
 *  - **fallback**: none first-class, but the per-product "Link out" toggle is on AND the variant
 *    can't be added to cart → all foreign offers;
 *  - **no policy** (`whereToBuyOfferRetailerIds` absent — channel rows unresolved) → all foreign.
 *
 * Before this, every offer the catalog returned was shown regardless of the app's bindings: the
 * catalog gates offers by the CATALOG binding only, never by WHERE_TO_BUY.
 */
const offersToShow = (
  foreign: CatalogV2Offer[],
  policy: { whereToBuyOfferRetailerIds?: number[] | null; whereToBuyFallback: boolean; isAddable: boolean },
): CatalogV2Offer[] => {
  if (!policy.whereToBuyOfferRetailerIds) return foreign;
  const linkOutEnabled = new Set(policy.whereToBuyOfferRetailerIds);
  const firstClass = foreign.filter((offer) => offer.retailerId != null && linkOutEnabled.has(offer.retailerId));
  if (firstClass.length) return firstClass;
  return policy.whereToBuyFallback && !policy.isAddable ? foreign : [];
};

const maxRewardByType = (rewards: ReturnType<typeof toReward>[] | undefined, campaignType: CampaignType) => {
  return rewards
    ?.filter((reward) => reward.campaignStatus === 'live' && reward.campaignType === campaignType)
    .reduce<ReturnType<typeof toReward> | undefined>((maxReward, reward) => {
      if (!maxReward || reward.rewardAmount > maxReward.rewardAmount) {
        return reward;
      }
      return maxReward;
    }, undefined);
};

export const adaptCatalogV2ProductToRewardSearchResult = (
  product: CatalogV2Product,
  requestedRetailerSku: string,
): ProductRewardSearchResult => {
  const variants = product.variants ?? [];
  const selectedVariant = variants.length
    ? variants.find((variant) =>
        variant.offers?.some((offer) => offer.isPrimary === true && offer.retailerSku === requestedRetailerSku),
      )
    : undefined;

  if (variants.length && !selectedVariant) {
    throw new Error('Error adapting Catalog V2 product: requested variant missing');
  }
  if (!variants.length && product.retailerSku !== requestedRetailerSku) {
    throw new Error('Error adapting Catalog V2 product: retailer SKU mismatch');
  }

  const selected = selectedVariant ?? {
    stacklineSku: product.stacklineSku,
    title: product.title ?? '',
    retailPrice: product.retailPrice,
    imageUrl: product.imageUrl,
    imageUrls: product.imageUrls,
    retailerProductPageUrl: product.retailerProductPageUrl,
    isAvailable: product.isAvailable,
    checkoutEligible: product.checkoutEligible,
    isLinkedToInventory: product.isLinkedToInventory,
    amazonSku: product.amazonSku,
    primeDeliveryTargetId: product.primeDeliveryTargetId,
    whereToBuyFallback: product.whereToBuyFallback,
    whereToBuyOfferRetailerIds: product.whereToBuyOfferRetailerIds,
    offers: product.offers,
  };
  const selectedOffer = selected.offers?.find(
    (offer) => offer.isPrimary === true && offer.retailerSku === requestedRetailerSku,
  );
  if (selectedVariant && !selectedOffer) {
    throw new Error('Error adapting Catalog V2 product: primary offer missing');
  }

  const imageUrls = selectedOffer?.imageUrls?.length
    ? selectedOffer.imageUrls
    : selected.imageUrls?.length
      ? selected.imageUrls
      : selected.imageUrl
        ? [selected.imageUrl]
        : [];
  const campaignRewards = product.campaignRewards?.map(toReward);
  const checkoutEligibility = toCheckoutEligibility(selected.checkoutEligible);
  const selectedStacklineSku = selectedOffer?.stacklineSku ?? selected.stacklineSku;
  const purchaseReward = maxRewardByType(campaignRewards, CampaignType.PurchaseRewardCampaign);
  const affiliateReward = maxRewardByType(campaignRewards, CampaignType.AffiliateRewardCampaign);
  const totalRewardAmount =
    product.rewardAmount && product.rewardAmount > 0
      ? product.rewardAmount
      : purchaseReward?.rewardAmount ?? affiliateReward?.rewardAmount ?? 0;

  const isAvailable = (selectedOffer?.inStock ?? selected.isAvailable) !== false;
  const retailPrice = asPrice(selectedOffer?.retailPrice ?? selected.retailPrice);
  const matchedOffers = offersToShow(selected.offers?.filter((offer) => offer !== selectedOffer) ?? [], {
    whereToBuyOfferRetailerIds: selected.whereToBuyOfferRetailerIds,
    whereToBuyFallback: selected.whereToBuyFallback === true,
    isAddable: checkoutEligibility === CheckoutEligibility.SERVER_SIDE && isAvailable && retailPrice > 0,
  });

  return {
    affiliateRewardAmount: affiliateReward?.rewardAmount ?? 0,
    brandId: product.brandId ?? 0,
    hasActiveInstantReward:
      campaignRewards?.some((reward) => reward.rewardUnlockStrategy === RewardUnlockStrategy.Instant) ?? false,
    imageUrls,
    retailerId: selectedOffer?.retailerId ?? product.retailerId ?? 0,
    retailerSku: requestedRetailerSku,
    retailerDisplayName: selectedOffer?.retailerName ?? product.retailerName,
    retailPrice,
    checkoutEligibility,
    isAvailable,
    isLinkedToInventory: selected.isLinkedToInventory,
    amazonSku: selected.amazonSku ?? undefined,
    primeDeliveryTargetId: selected.primeDeliveryTargetId ?? undefined,
    whereToBuyFallback: selected.whereToBuyFallback === true,
    hasFirstPartyOffer: checkoutEligibility === CheckoutEligibility.SERVER_SIDE,
    stacklineSku: selectedStacklineSku,
    title: selectedOffer?.title ?? selected.title ?? product.title,
    totalRewardAmount,
    scores: {
      maxPurchaseRewardAmount: purchaseReward?.rewardAmount,
      maxPurchaseRewardPercent: purchaseReward?.rewardPercent,
      maxAffiliateRewardAmount: affiliateReward?.rewardAmount,
      maxAffiliateRewardPercent: affiliateReward?.rewardPercent,
    },
    campaignRewards,
    matchedProducts: matchedOffers.map((offer) =>
      offerToMatchedProduct(offer, selected),
    ) as unknown as ProductRewardSearchResult['matchedProducts'],
    retailerProductPageUrl: selectedOffer?.retailerProductPageUrl ?? selected.retailerProductPageUrl,
  } as ProductRewardSearchResult;
};
